import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { AppLayout } from './components/layout/AppLayout';
import { AdminLayout } from './components/layout/AdminLayout';
import { AuthLayout } from './components/layout/AuthLayout';
import { PublicLayout } from './components/layout/PublicLayout';
import { Spinner } from './components/ui/Spinner';
import { ToastProvider } from './components/ui/Toast';
import { Landing } from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import Onboarding from './pages/Onboarding';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Monitors from './pages/Monitors';
import Notifications from './pages/Notifications';
import MonitorDetail from './pages/MonitorDetail';
import Settings from './pages/Settings';
import Subscription from './pages/Subscription';
import BookingRedirect from './pages/BookingRedirect';
import InviteLanding from './pages/InviteLanding';
import AuthRedirect from './pages/AuthRedirect';
import AccountDeleted from './pages/AccountDeleted';

import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminUserDetail from './pages/admin/AdminUserDetail';
import AdminPayments from './pages/admin/AdminPayments';
import AdminErrors from './pages/admin/AdminErrors';
import AdminDiscounts from './pages/admin/AdminDiscounts';
import Contact from './pages/Contact';
import Privacy from './pages/Privacy';

// Design System v2 — sandbox isolato, non in produzione (lazy)
const V2HomeTest = lazy(() => import('./design-v2/pages/HomeTest'));
const V2Vetrina = lazy(() => import('./design-v2/pages/Vetrina'));
const V2DesignSystem = lazy(() => import('./design-v2/pages/DesignSystem'));


/**
 * Guard that redirects unauthenticated users to /login.
 */
function ProtectedRoute() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}

/**
 * Guard that restricts access to admin users only.
 */
function AdminRoute() {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'admin' && user?.role !== 'super_admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
}

/**
 * Guard that redirects already-authenticated users away from auth pages.
 */
function GuestRoute() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes - landing page */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Landing />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<Privacy />} />
      </Route>

      {/* Public invite landing (standalone layout) */}
      <Route path="/invite/:code" element={<InviteLanding />} />

      {/* OAuth redirect landing — receives JWT tokens from backend */}
      <Route path="/auth-redirect" element={<AuthRedirect />} />

      {/* Public confirmation page after account deletion */}
      <Route path="/account-deleted" element={<AccountDeleted />} />

      {/* Design System v2 sandbox — isolato dal sito di produzione, no login */}
      <Route path="/v2/home-test" element={<Suspense fallback={null}><V2HomeTest /></Suspense>} />
      <Route path="/v2/vetrina" element={<Suspense fallback={null}><V2Vetrina /></Suspense>} />
      <Route path="/v2/design-system" element={<Suspense fallback={null}><V2DesignSystem /></Suspense>} />

      {/* Auth routes - login/register (guest only) */}
      <Route element={<GuestRoute />}>
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
        </Route>
      </Route>

      {/* Protected routes - app */}
      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayout />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/monitors" element={<Monitors />} />
          <Route path="/monitor/:id" element={<MonitorDetail />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/subscription" element={<Subscription />} />
        </Route>
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/book" element={<BookingRedirect />} />
      </Route>

      {/* Admin routes */}
      <Route element={<AdminRoute />}>
        <Route element={<AdminLayout />}>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/users" element={<AdminUsers />} />
          <Route path="/admin/users/:id" element={<AdminUserDetail />} />
          <Route path="/admin/payments" element={<AdminPayments />} />
          <Route path="/admin/errors" element={<AdminErrors />} />
          <Route path="/admin/discounts" element={<AdminDiscounts />} />
        </Route>
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <AppRoutes />
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
